import {
  CellClassParams,
  CellValueChangedEvent,
  ColDef,
  SelectionChangedEvent,
} from '@ag-grid-community/core'
import React, { useCallback, useRef, useState } from 'react'
import { updateCensusFileRows } from '../api/census-enrollment-platform-api-client'
import { CensusFileRowDto } from '../api/dto/census-file-row.dto'
import { CensusFileRowStatusEnum } from '../api/enums/census-file-row-status.enum'
import { CensusFileRowTypeEnum } from '../api/enums/census-file-row-type.enum'
import { PatchActionEnum } from '../api/enums/patch-action.enum'
import DownloadCensusReportButton from '../components/DownloadCensusReportButton'
import { ErrorTooltipRenderer } from '../components/ErrorTooltipRenderer'
import InlineEditActionButtons from '../components/InlineEditActionButtons'
import { LoadingOverlay } from '../components/LoadingOverlay/LoadingOverlay'
import ReviewRecordsBase from '../components/ReviewRecordsBase'
import ToggleEditModeButton from '../components/ToggleEditModeButton'
import WithdrawSelectedButton from '../components/WithdrawSelectedButton'
import { useMarkRowDirtyErrors } from '../hooks/use-mark-row-dirty-errors'
import { CensusFileRowGridData } from '../types/census-file-row-grid-data'
import { CensusManagementPageProps } from '../types/census-management-page-props'
import { configureEditField } from '../utils/configure-edit-field'
import { errorColumnStyling } from '../utils/error-column-styling'
import { mapGridDataToCensusFileRowDto } from '../utils/map-grid-data-to-census-file-row-dto'
import SendSelectedForReviewButton from '../components/SendSelectedForReviewButton'
import { getInProcessPageData } from '../utils/get-in-process-page-data'

// Column to display error keys with tooltips
const errorColumn: ColDef<CensusFileRowGridData> = {
  headerName: 'Errors',
  field: 'errors',
  sortable: true,
  width: 200,
  pinned: 'left',
  cellStyle: { color: 'red', fontWeight: 'bold' },
  cellRenderer: ErrorTooltipRenderer,
  // Format errors object for export (CSV/Excel), excluding ignored fields
  valueGetter: (params) => {
    const errors = params.data?.errors
    if (!errors || typeof errors !== 'object') return ''
    const ignoredFields = params.data?.updates?.ignoredFields ?? {}
    return Object.entries(errors)
      .filter(([field]) => !ignoredFields[field])
      .map(([field, error]: [string, any]) => {
        const message = error?.errorMessage || error
        return `${field}: ${message}`
      })
      .join('; ')
  },
}

// Only clear cross-page select-all when the user changes checkboxes, not when
// server-side pagination reloads row data (which can report fewer selected nodes).
const USER_SELECTION_SOURCES = new Set<
  SelectionChangedEvent<CensusFileRowGridData>['source']
>([
  'uiSelectAll',
  'uiSelectAllCurrentPage',
  'uiSelectAllFiltered',
  'checkboxSelected',
  'rowClicked',
  'spaceKey',
])

// Show errors on page
const ReviewRecordsError = (props: CensusManagementPageProps) => {
  const { markRowDirty, dirtyRows, cleanAllRows, undoLastEdit, editCount } =
    useMarkRowDirtyErrors()
  const [loading, setLoading] = useState(false) // Track submission state
  const [mode, setMode] = useState<'edit' | 'submit'>('submit')
  const { importPublicKey: publicKey } = props.routeParams
  const [isSelectAll, setIsSelectAll] = useState(false)
  const allRowsRef = useRef<CensusFileRowGridData[]>([])
  const isSelectAllRef = useRef(false)
  const selectAllGenerationRef = useRef(0)
  isSelectAllRef.current = isSelectAll

  const resetSelectAll = useCallback(() => {
    selectAllGenerationRef.current += 1
    setIsSelectAll(false)
    allRowsRef.current = []
  }, [])

  const handleSelectAll = useCallback(
    async (total: number) => {
      const generation = ++selectAllGenerationRef.current
      try {
        setLoading(true)
        const { data } = await getInProcessPageData({
          limit: total,
          page: 1,
          publicKey,
          censusRowType: CensusFileRowTypeEnum.ERROR,
          censusFileRowStatuses: [CensusFileRowStatusEnum.IN_PROCESS],
        })
        if (selectAllGenerationRef.current !== generation) {
          return
        }
        const allRows: CensusFileRowGridData[] = data.map((d) => ({
          ...d,
          ...d.rowData,
        }))
        allRowsRef.current = allRows
        setIsSelectAll(true)
      } catch (error) {
        console.error('Error fetching all rows for select all:', error)
        if (selectAllGenerationRef.current === generation) {
          resetSelectAll()
        }
      } finally {
        setLoading(false)
      }
    },
    [publicKey, resetSelectAll]
  )

  const handleSelectionChanged = useCallback(
    (
      selected: CensusFileRowGridData[],
      source: SelectionChangedEvent<CensusFileRowGridData>['source']
    ) => {
      // Server-side pagination can fire selectionChanged on page/sort reload with
      // fewer selected nodes even though the user did not deselect anything.
      if (!USER_SELECTION_SOURCES.has(source)) {
        return
      }

      // Header checked: onSelectAllCallback will fetch all rows and set isSelectAll.
      if (
        selected.length > 0 &&
        (source === 'uiSelectAll' ||
          source === 'uiSelectAllCurrentPage' ||
          source === 'uiSelectAllFiltered')
      ) {
        return
      }

      // Cross-page select-all: allRowsRef may hold 500 rows while the grid only
      // shows ~100 selected on the current page. Unchecking one row (99 < 500)
      // must exit select-all so bulk actions don't still target every row.
      const isPartialDeselect =
        isSelectAllRef.current &&
        selected.length > 0 &&
        allRowsRef.current.length > 0 &&
        selected.length < allRowsRef.current.length

      // Clear cross-page select-all when the user deselects, including while a
      // select-all fetch is still in flight (isSelectAll may still be false).
      if (selected.length === 0 || isPartialDeselect) {
        resetSelectAll()
      }
    },
    [resetSelectAll]
  )

  const handleSubmitComplete = useCallback(
    (resetGrid: () => void) => {
      resetSelectAll()
      resetGrid()
    },
    [resetSelectAll]
  )

  // Update rows with new data
  const submitCorrections = async (rowsToSubmit: CensusFileRowGridData[]) => {
    const updatedRows: CensusFileRowDto[] = rowsToSubmit.map((row) =>
      mapGridDataToCensusFileRowDto(row)
    )
    await updateCensusFileRows(
      updatedRows,
      publicKey,
      PatchActionEnum.UPDATE_RECORDS
    )
  }

  const handleCellValueChanged = useCallback(
    (params: CellValueChangedEvent<CensusFileRowGridData>) => {
      markRowDirty(params)
    },
    []
  )

  const modifyColDefs = (
    baseColDefs: ColDef<CensusFileRowGridData>[]
  ): ColDef<CensusFileRowGridData>[] => {
    return [
      {
        headerCheckboxSelection: mode === 'submit', // Allow selection only in Submit Mode
        checkboxSelection: mode === 'submit',
        width: 50,
        pinned: 'left',
      },
      errorColumn, // Display errors summary
      ...baseColDefs.map((col) => {
        const errorStyling = errorColumnStyling(col.field, mode)
        return {
          ...col,
          ...configureEditField(col.field, mode),
          ...errorStyling,
          editable: (params) => {
            const isIgnored =
              !!params.data?.updates?.ignoredFields?.[col.field ?? '']
            return mode === 'edit' && !isIgnored
          },
          cellStyle: (params: CellClassParams<CensusFileRowGridData>) => {
            const field = col.field ?? ''
            if (params.data?.updates?.ignoredFields?.[field]) {
              return {
                backgroundColor: '#fff7e6',
                color: '#ad8b00',
                opacity: 0.75,
              }
            }
            return errorStyling.cellStyle?.(params)
          },
        }
      }),
    ]
  }

  return (
    <>
      <LoadingOverlay isLoading={loading} message="Loading..." />
      <ReviewRecordsBase
        tabKey={CensusFileRowTypeEnum.ERROR}
        {...props}
        rowQueryParams={{
          censusFileRowStatuses: [CensusFileRowStatusEnum.IN_PROCESS],
          censusRowType: CensusFileRowTypeEnum.ERROR,
        }}
        modifyColDefs={modifyColDefs} // Pass the callback function here
        onSelectionChangedCallback={handleSelectionChanged}
        onSelectAllCallback={handleSelectAll}
        actionButton={({ censusRows, handleSubmit, selectedRows, counts }) => {
          const onSubmitComplete = () => handleSubmitComplete(handleSubmit)
          const effectiveSelectedRows = isSelectAll
            ? allRowsRef.current
            : selectedRows
          return (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <ToggleEditModeButton
                dirtyRows={dirtyRows}
                selectedRows={effectiveSelectedRows}
                mode={mode}
                setMode={setMode}
              />
              {mode === 'edit' ? (
                <InlineEditActionButtons
                  undoLastEdit={undoLastEdit}
                  editCount={editCount}
                  dirtyRows={dirtyRows}
                  handleSubmit={onSubmitComplete}
                  cleanAllRows={cleanAllRows}
                  submitCorrections={submitCorrections}
                  updateMode={false}
                  setMode={setMode}
                  setLoading={setLoading}
                />
              ) : (
                <>
                  <SendSelectedForReviewButton
                    publicKey={publicKey}
                    selectedRows={effectiveSelectedRows}
                    handleSubmit={onSubmitComplete}
                    cleanAllRows={cleanAllRows}
                    setLoading={setLoading}
                  />
                  <WithdrawSelectedButton
                    publicKey={publicKey}
                    selectedRows={effectiveSelectedRows}
                    handleSubmit={() => {
                      setMode('submit')
                      onSubmitComplete()
                    }}
                    cleanAllRows={cleanAllRows}
                    setLoading={setLoading}
                    allRows={censusRows}
                    counts={counts}
                  />
                  <DownloadCensusReportButton
                    publicKey={publicKey}
                    rowType={CensusFileRowTypeEnum.ERROR}
                  />
                </>
              )}
            </div>
          )
        }}
        handleCellValueChanged={handleCellValueChanged}
      />
    </>
  )
}

export default ReviewRecordsError
